"use strict";

var _interopRequireWildcard = require("@babel/runtime/helpers/interopRequireWildcard").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = exports.ColorSelector = void 0;
var _web = require("@react-hookz/web");
var _lodestarIcons = require("@smartsheet/lodestar-icons");
var _lodestarUtils = require("@smartsheet/lodestar-utils");
var _react = _interopRequireWildcard(require("react"));
var _i18n = require("../../../i18n/i18n");
var _useTheme = require("../../../theme/utils/useTheme");
var _button = require("../../button");
var _colorPickerTheme = require("../colorPickerTheme");
var _colorUtilities = require("../colorPickerUtils/colorUtilities");
var _warnOnLocaleProp = require("../colorPickerUtils/warnOnLocaleProp");
var _ColorPickerCatalogs = require("../i18n/ColorPickerCatalogs");
var _colorPalettes = require("./colorPalettes");
var _AddButton = require("./components/AddButton");
var _ColorButton = require("./components/ColorButton");
var _AutomaticButtonWrap = require("./styled/AutomaticButtonWrap");
var _ColorSelectorRoot = require("./styled/ColorSelectorRoot");
var _CustomSectionTitle = require("./styled/CustomSectionTitle");
var _CustomSectionWrapper = require("./styled/CustomSectionWrapper");
const warnOnNoLocaleProp = (0, _warnOnLocaleProp.makeWarnOnLocaleProp)("ColorSelector");
function compareColors() {
  let c1 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : "";
  let c2 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : "";
  return c1.toLowerCase() === c2.toLowerCase();
}

// Fancy props (i.e. w/ the fancy object return)

const ColorSelector = _ref => {
  let {
    automaticColor,
    className,
    clientId,
    currentColor,
    customColors: providedCustomColors,
    forceReturnType,
    locale,
    maxCustomColors = 20,
    onAddButtonClick,
    onChange,
    onRemoveCustomColor,
    standardColorPalette,
    showCustom
  } = _ref;
  if (process.env.NODE_ENV !== "production") {
    if (typeof locale !== "string") {
      warnOnNoLocaleProp();
    }
    if (showCustom && !onRemoveCustomColor) {
      console.warn("To use custom colors, these three props must be present: showCustom, onRemoveCustomColor, and customColors.");
    }
  }
  const [activeIndex, setActiveIndex] = (0, _react.useState)(-1);
  const addBtnRef = (0, _react.useRef)(null);
  const autoBtnRef = (0, _react.useRef)(null);
  const references = (0, _react.useMemo)(() => [], []);
  const {
    key
  } = (0, _useTheme.useTheme)();
  const themeMode = key;
  const standardColorsToUse = standardColorPalette === "legacy" ? _colorPalettes.legacyColors : _colorPalettes.lodestarColors;
  // Memoize this to make it stable across renders. If we don't, the effects below might think the
  // set of custom colors has changed and will try and update states based on the changes. At best
  // this leads to unecessary re-renders, at worst it breaks keyboard nav.
  const customColors = (0, _react.useMemo)(() => providedCustomColors || [], [providedCustomColors]);
  const getSelectedColorIndex = (0, _react.useCallback)(() => {
    return standardColorsToUse.concat(customColors || []).findIndex(colorStr => colorStr.toUpperCase() === currentColor.toUpperCase());
  }, [currentColor, customColors, standardColorsToUse]);
  const colorIndex = getSelectedColorIndex();
  const indexThatCanBeTabbed = Math.max(0, colorIndex);
  const dispatchChange = value => {
    const colorDefs = (0, _colorUtilities.colorParser)(value);
    const {
      rgba,
      hex,
      a
    } = colorDefs;
    switch (forceReturnType) {
      case "hex":
        onChange((0, _colorUtilities.rgb2hex)({
          ...colorDefs
        }));
        break;
      case "rgba":
        onChange(rgba);
        break;
      case "full":
        onChange(colorDefs);
        break;
      default:
        onChange(a && a > -1 ? rgba : hex);
        break;
    }
  };
  const updateFocusedButton = (0, _react.useCallback)(newActiveIndex => {
    // Just be safe so we don't accidentally break everything.
    if (newActiveIndex >= 0) {
      setActiveIndex(newActiveIndex);
      references[newActiveIndex].current?.focus();
    }
  }, [references]);
  const colorKeyDownHandler = e => {
    let newIndex;
    const standardColors = standardColorsToUse;
    switch (e.key) {
      case "ArrowLeft":
        e.preventDefault();
        newIndex = activeIndex - 1;
        if (newIndex < 0) {
          newIndex = references.length - 1;
        }
        updateFocusedButton(newIndex);
        break;
      case "ArrowRight":
        e.preventDefault();
        newIndex = activeIndex + 1;
        if (newIndex > references.length - 1) {
          newIndex = 0;
        }
        updateFocusedButton(newIndex);
        break;
      case "ArrowDown":
        e.preventDefault();
        newIndex = activeIndex + 8;
        if (newIndex > references.length - 1) {
          newIndex = newIndex % 8;
        }
        updateFocusedButton(newIndex);
        break;
      case "ArrowUp":
        {
          e.preventDefault();
          newIndex = activeIndex - 8;
          if (newIndex < 0) {
            const totalColors = customColors.length + standardColors.length + 1;
            const lastIndex = totalColors - 1;
            const remainder = 8 - totalColors % 8;
            newIndex = totalColors + remainder - (8 - activeIndex);
            if (newIndex > lastIndex) {
              newIndex -= 8;
            }
          }
          updateFocusedButton(newIndex);
          break;
        }
      case " ":
        {
          const value = e.target.value;
          if (value) {
            dispatchChange(value);
          }
          break;
        }
      default:
        return e;
    }
  };

  // Focus the first color button when the component mounts. We really only want to run this once,
  // so make sure the dependencies are set up in the first render.
  (0, _web.useMountEffect)(() => {
    if (automaticColor) {
      autoBtnRef.current?.focus();
    } else {
      references[indexThatCanBeTabbed].current?.focus();
    }
  });
  const prevCurrentColor = (0, _web.usePrevious)(currentColor);
  (0, _react.useEffect)(() => {
    // Cover edge case where color is changed programtically while picker is open
    if (currentColor !== prevCurrentColor) {
      const colorIndex = getSelectedColorIndex();
      // Only focus the color if the ColorSelector has focus and the selected color is
      // actually one of the standard colors.
      references.forEach(ref => {
        if (document.activeElement === ref.current) {
          updateFocusedButton(colorIndex);
        }
      });
    }
  }, [prevCurrentColor, currentColor, getSelectedColorIndex, references, updateFocusedButton]);
  const renderColorButton = function (i18n, color, index) {
    let removable = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : false;
    const btnRef = /*#__PURE__*/_react.default.createRef();
    references[index] = btnRef;
    return /*#__PURE__*/_react.default.createElement(_ColorButton.ColorButton, {
      key: `${color}${String(index)}`,
      "aria-label": i18n._( /*i18n*/"Select color {color}", {
        color
      }),
      color: color,
      selected: compareColors(currentColor, color),
      onClick: dispatchChange,
      onFocus: () => setActiveIndex(index),
      onKeyDown: colorKeyDownHandler,
      tabIndex: index === indexThatCanBeTabbed ? 0 : -1,
      ref: btnRef,
      clientId: (0, _lodestarUtils.getClientId)(clientId, `cp-cs-clr-btn-${color}`),
      onRemove: removable ? onRemoveCustomColor : undefined
    });
  };
  if (showCustom) {
    references[standardColorsToUse.length + customColors.length] = addBtnRef;
  }
  return /*#__PURE__*/_react.default.createElement(_i18n.I18nContextProvider, {
    locale: locale
  }, /*#__PURE__*/_react.default.createElement(_i18n.I18NContextConsumer, null, _ref2 => {
    let {
      i18n
    } = _ref2;
    return /*#__PURE__*/_react.default.createElement(_ColorSelectorRoot.ColorSelectorRoot, {
      className: className,
      "data-client-id": clientId,
      themeMode: themeMode
    }, automaticColor && /*#__PURE__*/_react.default.createElement(_AutomaticButtonWrap.AutomaticButtonWrap, {
      themeMode: themeMode
    }, /*#__PURE__*/_react.default.createElement(_button.Button, {
      ref: autoBtnRef,
      onClick: () => dispatchChange(automaticColor),
      appearance: "secondary",
      shouldFitContainer: true,
      clientId: (0, _lodestarUtils.getClientId)(clientId, "cp-cs-auutomatic-btn"),
      type: "button"
    }, i18n._( /* i18n */"Automatic"))), standardColorsToUse.map((color, index) => renderColorButton(i18n, color, index)), showCustom && onAddButtonClick && /*#__PURE__*/_react.default.createElement(_CustomSectionWrapper.CustomSectionWrapper, null, /*#__PURE__*/_react.default.createElement(_CustomSectionTitle.CustomSectionTitle, {
      themeMode: themeMode,
      system: themeMode.split("-")[0] || "lodestar",
      variant: _colorPickerTheme.colorPickerTheme.colorSelector.customSection.variant(themeMode)
    }, i18n._( /* i18n */"Custom")), /*#__PURE__*/_react.default.createElement(_AddButton.AddButton, {
      onFocus: () => setActiveIndex(standardColorsToUse.length + customColors.length),
      onKeyDown: colorKeyDownHandler,
      ref: addBtnRef,
      disabled: customColors.length >= maxCustomColors,
      onClick: onAddButtonClick,
      clientId: (0, _lodestarUtils.getClientId)(clientId, "cp-cs-add-btn"),
      toolTipText: i18n._( /* i18n */
      "You’ve reached the {maxCustomColors} custom colors limit. To add another color, remove one first.", {
        maxCustomColors: maxCustomColors
      })
    }, /*#__PURE__*/_react.default.createElement(_lodestarIcons.AddIcon, {
      size: "xxSmall"
    })), customColors && customColors.map((color, index) => renderColorButton(i18n, color, index + standardColorsToUse.length, true))));
  }));
};
exports.ColorSelector = ColorSelector;
var _default = (0, _i18n.withTranslationInjection)(ColorSelector, _ColorPickerCatalogs.injectCatalog);
exports.default = _default;
//# sourceMappingURL=ColorSelector.js.map